/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

import React, { useEffect } from 'react';
import styles from './styles.module.scss';
import cx from 'classnames';
import NextImage from '@/components/NextImage';
type ModalThumbnailsProps = {
  images: string[] | undefined | null;
  imageIndex: number;
  setImageIndex: (newIndex: number) => void;
  containerRef: React.RefObject<HTMLDivElement>;
  photoCaptions: string[] | null | undefined;
};

export const ModalThumbnails: React.FC<ModalThumbnailsProps> = ({
  images,
  imageIndex,
  setImageIndex,
  containerRef,
  photoCaptions,
}) => {
  useEffect(() => {
    if (containerRef.current) {
      const container = containerRef.current;
      const children = container.children;
      const selectedImage = children[imageIndex] as HTMLElement;

      if (selectedImage) {
        const containerRect = container.getBoundingClientRect();
        const selectedRect = selectedImage.getBoundingClientRect();

        const scrollLeft =
          selectedRect.left -
          containerRect.left -
          (containerRect.width / 2 - selectedRect.width / 2);

        container.scrollTo({
          left: container.scrollLeft + scrollLeft,
          behavior: 'smooth',
        });
      }
    }
  }, [containerRef, imageIndex]);
  return (
    <div className={styles.modalThumbnails} ref={containerRef}>
      {images?.map((image, index) => {
        return (
          <button
            key={`image-${image}`}
            onClick={() => {
              setImageIndex(index);
            }}
            className={cx({ [styles.activeImage]: index === imageIndex })}
            aria-label={`Select image ${index + 1}`}
            aria-pressed={index === imageIndex}
          >
            <NextImage
              src={image}
              alt={photoCaptions?.[index] || 'Review Image' + `${index + 1}`}
              width={72}
              height={72}
              className={styles.thumbnailImages}
            />
          </button>
        );
      })}
    </div>
  );
};
